import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  LinearProgress,
  TableHead,
  TableRow,
  Collapse,
  Box,
  Button,
  CircularProgress,
  TextField,
  Autocomplete,
} from "@mui/material";
import SearchCustomTable from "./search";
import AddIcon from "@mui/icons-material/Add";

const styles = {
  select: {
    "& .MuiOutlinedInput-notchedOutline": { border: "none" },
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
      backgroundColor: "#f9f9f9",
    },
    "& .MuiInputLabel-root": { color: "#555" },
    "&.Mui-disabled .MuiOutlinedInput-root": { backgroundColor: "#f0f0f0" },
  },
};

function FilterAutocomplete({
  label,
  options,
  onChange,
  value,
  getOptionLabel,
  loading,
  multiple = true,
  disabled = false,
}) {
  return (
    <Autocomplete
      disabled={disabled}
      style={{ opacity: disabled ? 0.5 : 1 }}
      sx={styles.select}
      multiple={multiple}
      options={options}
      value={value}
      onChange={onChange}
      getOptionLabel={getOptionLabel}
      renderInput={(params) => (
        <>
          <TextField
            {...params}
            label={loading ? <CircularProgress size={20} /> : label}
          />
        </>
      )}
    />
  );
}

const CustomTable = ({
  columns,
  data,
  page,
  tableLoading,
  search,
  inputText,
  setInputText,
  totalCount,
  handleChangePage,
  addNew,
  addNewFunction,
  addNewTitle,
  secondAddNew,
  secondAddNewFunction,
  secondAddNewTitle,
  expandedRows,
  onToggleRow,
  allowToggleRow = false,
  rowsPerPage = 10,
  disabled = false,
  filters = false,
  campaignFilter = false,
  tags,
  filterTag,
  setFilterTag,
  categories,
  filterCategory,
  setFilterCategory,
  locations,
  filterLocation,
  setFilterLocation,
  network,
  filterNetwork,
  setFilterNetwork,
  counties,
  filterCounty,
  setFilterCounty,
  cities,
  filterCity,
  setFilterCity,
  forDevices = false,
  partners,
  filterPartner,
  setFilterPartner,
  campaigns,
  filterCampaign,
  setFilterCampaign,
  clearFilters,
  fromDevices = false,
}) => {
  const isRowExpanded = (id) => expandedRows?.includes(id);

  return (
    <>
      {filters && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            rowGap: 10,
            alignItems: "flex-start",
            marginBottom: 20,
          }}
        >
          {forDevices && (
            <Box sx={{ width: 300 }}>
              <FilterAutocomplete
                disabled={disabled}
                label="Selecteaza Retea"
                options={network}
                value={
                  network?.filter((n) => filterNetwork.includes(n.ID)) || []
                }
                onChange={(event, value) => {
                  setFilterNetwork(value.map((v) => v.ID));
                }}
                getOptionLabel={(option) => option?.name || ""}
                isOptionEqualToValue={(option, value) => option.ID === value.ID}
              />
            </Box>
          )}

          {forDevices && (
            <Box sx={{ width: 300 }}>
              <FilterAutocomplete
                disabled={disabled}
                label="Selecteaza Judet"
                options={counties}
                value={
                  counties?.filter((c) => filterCounty.includes(c.ID)) || []
                }
                onChange={(event, value) => {
                  setFilterCounty(value.map((v) => v.ID));
                }}
                getOptionLabel={(option) => option?.name || ""}
                isOptionEqualToValue={(option, value) => option.ID === value.ID}
                loading={false}
              />
            </Box>
          )}

          {forDevices && (
            <Box sx={{ width: 300 }}>
              <FilterAutocomplete
                disabled={disabled}
                label="Selecteaza Oras"
                options={cities}
                value={
                  cities?.filter((c) => filterCity.includes(c.ID)) || []
                }
                onChange={(event, value) => {
                  setFilterCity(value.map((v) => v.ID));
                }}
                getOptionLabel={(option) => option?.name || ""}
                isOptionEqualToValue={(option, value) => option.ID === value.ID}
                loading={false}
              />
            </Box>
          )}

          <Box sx={{ width: 300 }}>
            <FilterAutocomplete
              disabled={disabled}
              label="Selecteaza Partener"
              options={partners}
              value={
                partners?.filter((p) => filterPartner.includes(p.ID)) || []
              }
              onChange={(event, value) => {
                setFilterPartner(value.map((v) => v.ID));
              }}
              getOptionLabel={(option) => option?.name || ""}
              isOptionEqualToValue={(option, value) => option.ID === value.ID}
              loading={false}
            />
          </Box>

          <Box sx={{ width: 300 }}>
            <FilterAutocomplete
              disabled={disabled}
              label="Selecteaza Locatie"
              options={locations}
              value={
                locations?.filter((loc) => filterLocation.includes(loc.ID)) ||
                []
              }
              onChange={(event, value) => {
                setFilterLocation(value.map((v) => v.ID));
              }}
              getOptionLabel={(option) => option?.name || ""}
              isOptionEqualToValue={(option, value) => option.ID === value.ID}
              loading={false}
            />
          </Box>

          <Box sx={{ width: 300 }}>
            <FilterAutocomplete
              disabled={disabled}
              label="Selecteaza Tag"
              options={tags}
              value={tags?.filter((tag) => filterTag.includes(tag.ID)) || []}
              onChange={(event, value) => {
                setFilterTag(value.map((v) => v.ID));
              }}
              getOptionLabel={(option) => option?.name || ""}
              isOptionEqualToValue={(option, value) => option.ID === value.ID}
              loading={false}
            />
          </Box>

          {categories && filterCategory && setFilterCategory && (
            <Box sx={{ width: 300 }}>
              <FilterAutocomplete
                disabled={disabled}
                label="Selecteaza Categorie"
                options={categories}
                value={
                  categories?.filter((category) =>
                    filterCategory.includes(category.ID)
                  ) || []
                }
                onChange={(event, value) => {
                  setFilterCategory(value.map((v) => v.ID));
                }}
                getOptionLabel={(option) => option?.name || ""}
                isOptionEqualToValue={(option, value) => option.ID === value.ID}
                loading={false}
              />
            </Box>
          )}

          {campaignFilter && (
            <Box sx={{ width: 300 }}>
              <FilterAutocomplete
                disabled={disabled}
                label="Selecteaza Campanie"
                options={campaigns}
                value={
                  campaigns?.filter((c) => filterCampaign.includes(c.ID)) || []
                }
                onChange={(event, value) => {
                  setFilterCampaign(value.map((v) => v.ID));
                }}
                getOptionLabel={(option) => option?.name || ""}
                isOptionEqualToValue={(option, value) => option.ID === value.ID}
                loading={false}
              />
            </Box>
          )}

          <div style={{ display: "flex", columnGap: 10, width: 300 }}>
            <Button
              variant="outlined"
              size="small"
              sx={{
                textTransform: "none",
                borderColor: "gray",
                color: "gray",
                "&:hover": {
                  backgroundColor: "#f9f9f9",
                  borderColor: "#aaa",
                },
                width: "100%",
              }}
              onClick={() => {
                clearFilters();
              }}
            >
              Reseteaza Filtre
            </Button>
          </div>
        </div>
      )}
      <div className="custom-table-container">
        <div className="search-and-add">
          <div style={{ width: "35%" }}>
            {search && (
              <SearchCustomTable
                inputText={inputText}
                setInputText={setInputText}
              />
            )}
          </div>
          <div style={{ display: "flex", columnGap: 10 }}>
            {secondAddNew && (
              <div
                style={{
                  marginTop: !search && "0.75vw",
                  opacity: disabled ? 0.5 : 1,
                }}
                className={`new-asset-btn ${disabled ? "disabled" : ""}`}
                onClick={!disabled ? secondAddNewFunction : undefined}
              >
                <AddIcon style={{ fontSize: "1rem", marginTop: "1px" }} />
                <div className="new-asset-btn-text">{secondAddNewTitle}</div>
              </div>
            )}
            {addNew && (
              <div
                style={{
                  marginTop: !search ? "0.75vw" : undefined,
                  opacity: disabled ? 0.5 : 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                className={`new-asset-btn ${disabled ? "disabled" : ""}`}
                onClick={!disabled ? addNewFunction : undefined}
              >
                {fromDevices ? (
                  <span
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    {addNewTitle}
                  </span>
                ) : (
                  <>
                    <AddIcon style={{ fontSize: "1rem", marginTop: "1px" }} />
                    <div className="new-asset-btn-text">{addNewTitle}</div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
        <TableContainer sx={{ maxWidth: "100%", overflowX: "auto" }}>
          <Table sx={{ tableLayout: "fixed", minWidth: "1120px" }}>
            <TableHead>
              <TableRow>
                {columns?.map((column) => (
                  <TableCell
                    key={column.field}
                    align="center"
                    onClick={column.onHeaderClick}
                    sx={{
                      width: column.width,
                      fontSize: 15,
                      color: "rgba(35, 43, 53, 0.8)",
                      fontFamily: "SeagoeUIBold",
                      cursor: column.onHeaderClick ? "pointer" : "default",
                      paddingTop: "8px",
                      paddingBottom: "8px",
                    }}
                  >
                    {column.headerName}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {tableLoading ? (
                <TableRow>
                  <TableCell
                    sx={{ paddingTop: "6px", paddingBottom: "6px" }}
                    colSpan={columns.length}
                    align="center"
                  >
                    <div style={{ margin: 25, marginBlock: 30 }}>
                      <LinearProgress
                        color="inherit"
                        style={{
                          alignSelf: "center",
                          width: 200,
                          textAlign: "center",
                        }}
                      />
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                data?.map((row, rowIndex) => (
                  <React.Fragment key={`fragment-${row.id || rowIndex}`}>
                    <TableRow
                      key={`row-${row.id || rowIndex}`}
                      onClick={() =>
                        allowToggleRow && onToggleRow(row.id || rowIndex)
                      }
                      sx={{
                        cursor:
                          row.sublocations?.length > 0 ? "pointer" : "default",
                      }}
                    >
                      {columns?.map((column, colIndex) => (
                        <TableCell
                          sx={{ paddingTop: "6px", paddingBottom: "6px" }}
                          key={`${row.id || rowIndex}-${
                            column.field || colIndex
                          }`}
                          align="center"
                        >
                          {column.renderCell
                            ? column.renderCell({ row })
                            : row[column.field]}
                        </TableCell>
                      ))}
                    </TableRow>

                    <TableRow key={`collapse-${row.id || rowIndex}`}>
                      <TableCell
                        colSpan={columns.length}
                        style={{ padding: 0, borderBottom: 0 }}
                      >
                        <Collapse
                          in={isRowExpanded(row.id || rowIndex)}
                          timeout="auto"
                          unmountOnExit
                        >
                          <Table
                            size="small"
                            sx={{
                              tableLayout: "fixed",
                              backgroundColor: "#ffffff",
                              borderRadius: "8px",
                              width: "100%",
                            }}
                          >
                            <TableBody>
                              {row.sublocations?.map(
                                (sublocation, subIndex) => (
                                  <TableRow
                                    key={`${row.id || rowIndex}-${
                                      sublocation.id || subIndex
                                    }`}
                                    sx={{
                                      "&:hover": {
                                        backgroundColor: "#f4f4f4",
                                      },
                                      transition: "background-color 0.3s ease",
                                    }}
                                  >
                                    {columns?.map((column, colIndex) => (
                                      <TableCell
                                        key={`${sublocation.id || subIndex}-${
                                          column.field || colIndex
                                        }`}
                                        align="center"
                                        sx={{
                                          fontSize: "14px",
                                          color: "#555",
                                          fontFamily: "SeagoeUI",
                                          padding: "10px 16px",
                                          borderBottom: "1px solid #e0e0e0",
                                        }}
                                      >
                                        {column.renderCell
                                          ? column.renderCell({
                                              row: sublocation,
                                            })
                                          : sublocation[column.field]}
                                      </TableCell>
                                    ))}
                                  </TableRow>
                                )
                              )}
                            </TableBody>
                          </Table>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            sx={{ display: "inline-flex" }}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPageOptions={[]}
            showFirstButton={true}
            showLastButton={true}
          />
        </TableContainer>
      </div>
    </>
  );
};

export default CustomTable;
